import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdBusiness,
    MdCloudUpload,
    MdEmail,
    MdImage,
    MdLanguage,
    MdLocationOn,
    MdPerson,
} from 'react-icons/md'
import { SearchableSelect } from '@/components/forms/searchable-select'
import { FaWhatsapp } from 'react-icons/fa'
import { updateCompany } from '@/lib/api/companies/companies-api'
import { SERVICE_LIST_ITEMS } from '@/lib/constants/service-list-items'
import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import { getFileUrl } from '@/lib/utils/file-url'
import { companyNameLabel, isIndividualCompany } from '@/lib/person-type'
import { CompanySegment } from '@/lib/api/common/interfaces/company.interface'
import {
    setAccessibleCompanies,
    setCredentials,
} from '@/store/slices/authSlice'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'
import { useLogoUpload } from '../use-logo-upload'
import { formatCpf, formatCnpj, formatPhone, formatCep } from '../account-utils'

/** Aba "Empresa": dados cadastrais e fiscais da empresa ativa. */
export default function CompanyTab() {
    const auth = useAppSelector(state => state.auth)
    const { user, isMaster, isCompany, isPartner } = useAuth()
    const dispatch = useAppDispatch()
    const canEditCompany = isMaster || isCompany
    const { uploadingLogo, logoPreview, handleLogoUpload, logoCropDialog } =
        useLogoUpload()
    const logoInputRef = useRef<HTMLInputElement>(null)

    const isHealthSegment =
        (user?.company as any)?.segment === CompanySegment.HEALTH
    const isCompanyIndividual = isIndividualCompany(user?.company)

    const [loadingCompany, setLoadingCompany] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    const [companyData, setCompanyData] = useState({
        name: '',
        tradingName: '',
        aboutUs: '',
        email: '',
        website: '',
        cnpj: '',
        cpf: '',
        stateRegistration: '',
        municipalRegistration: '',
        serviceListItem: '',
        taxRegime: '',
        zipCode: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        cityCode: '',
        state: '',
        country: 'BR',
        segment: '' as CompanySegment | '',
        secondarySegment: '',
        whatsappNumber: '',
    })

    useEffect(() => {
        if (!user) return

        if (user.company) {
            const company = user.company as any
            console.log('[AccountPage] Company data received:', company)
            console.log('[AccountPage] aboutUs value:', company.aboutUs)

            const cnpjValue = company.cnpj ?? ''
            const formattedCnpj = cnpjValue ? formatCnpj(cnpjValue) : ''

            const newCompanyData = {
                name: company.name ?? '',
                tradingName: company.tradingName ?? '',
                aboutUs: company.aboutUs ?? '',
                email: company.email ?? '',
                website: company.website ?? '',
                cnpj: formattedCnpj,
                cpf: company.cpf ? formatCpf(company.cpf) : '',
                stateRegistration: company.stateRegistration ?? '',
                municipalRegistration: company.municipalRegistration ?? '',
                serviceListItem: company.serviceListItem ?? '',
                taxRegime: company.taxRegime ?? '',
                zipCode: company.zipCode ?? '',
                street: company.street ?? '',
                number: company.number ?? '',
                complement: company.complement ?? '',
                neighborhood: company.neighborhood ?? '',
                city: company.city ?? '',
                cityCode: company.cityCode ?? '',
                state: company.state ?? '',
                country: company.country ?? 'BR',
                segment: company.segment ?? '',
                secondarySegment: company.secondarySegment ?? '',
                whatsappNumber: company.whatsappNumber ?? '',
            }

            console.log(
                '[AccountPage] Setting companyData with values:',
                newCompanyData
            )
            setCompanyData(newCompanyData)

            // Auto-preencher cityCode se empresa tem CEP mas não tem código do município
            if (company.zipCode && !company.cityCode) {
                const cleanCep = company.zipCode.replace(/\D/g, '')
                if (cleanCep.length === 8) {
                    fetchPostalCode(cleanCep)
                        .then(address => {
                            if (address.ibge) {
                                setCompanyData(prev => ({
                                    ...prev,
                                    cityCode: address.ibge,
                                }))
                            }
                        })
                        .catch(() => {})
                }
            }
        } else if (user.companyId) {
            // Se tiver companyId mas não tiver company carregada, resetar os campos
            setCompanyData({
                name: '',
                tradingName: '',
                aboutUs: '',
                email: '',
                website: '',
                cnpj: '',
                cpf: '',
                stateRegistration: '',
                municipalRegistration: '',
                serviceListItem: '',
                taxRegime: '',
                zipCode: '',
                street: '',
                number: '',
                complement: '',
                neighborhood: '',
                city: '',
                cityCode: '',
                state: '',
                country: 'BR',
                segment: '',
                secondarySegment: '',
                whatsappNumber: '',
            })
        }
    }, [user])

    const handleUpdateCompany = async () => {
        if (!user?.companyId) return

        // Conta de pessoa física não tem CNPJ — exigir aqui deixava o PF sem como
        // completar o cadastro (o RegistrationGuard bloqueava o painel pedindo o
        // CNPJ, e esta tela recusava salvar sem ele).
        const requiredFields: { value: string; label: string }[] = [
            isCompanyIndividual
                ? { value: companyData.cpf, label: 'CPF' }
                : { value: companyData.cnpj, label: 'CNPJ' },
            { value: companyData.street, label: 'Rua' },
            { value: companyData.number, label: 'Número' },
            { value: companyData.neighborhood, label: 'Bairro' },
            { value: companyData.city, label: 'Cidade' },
            { value: companyData.state, label: 'Estado' },
            { value: companyData.zipCode, label: 'CEP' },
        ]

        const emptyFields = requiredFields.filter(f => !f.value?.trim())
        if (emptyFields.length > 0) {
            toast.warning(
                `Preencha os campos obrigatórios: ${emptyFields.map(f => f.label).join(', ')}`
            )
            return
        }

        setLoadingCompany(true)
        try {
            const cnpjValue = companyData.cnpj.trim()
            const cleanCnpj = cnpjValue
                ? cnpjValue.replace(/\D/g, '')
                : undefined

            const cpfValue = companyData.cpf.trim()
            const cleanCpf = cpfValue ? cpfValue.replace(/\D/g, '') : undefined

            const response = await updateCompany(user.companyId, {
                name: companyData.name.trim() || undefined,
                tradingName: companyData.tradingName.trim() || undefined,
                aboutUs: companyData.aboutUs.trim() || undefined,
                email: companyData.email.trim() || undefined,
                website: companyData.website.trim() || undefined,
                // Só um dos dois documentos vai: o backend recusa CNPJ em conta de
                // pessoa física, e os campos fiscais não existem para ela.
                cnpj: isCompanyIndividual ? undefined : cleanCnpj || undefined,
                cpf: isCompanyIndividual ? cleanCpf || undefined : undefined,
                stateRegistration: isCompanyIndividual
                    ? undefined
                    : companyData.stateRegistration.trim() || undefined,
                municipalRegistration: isCompanyIndividual
                    ? undefined
                    : companyData.municipalRegistration.trim() || undefined,
                serviceListItem: isCompanyIndividual
                    ? undefined
                    : companyData.serviceListItem.trim() || undefined,
                taxRegime: isCompanyIndividual
                    ? undefined
                    : companyData.taxRegime.trim() || undefined,
                zipCode: companyData.zipCode.trim() || undefined,
                street: companyData.street.trim() || undefined,
                number: companyData.number.trim() || undefined,
                complement: companyData.complement.trim() || undefined,
                neighborhood: companyData.neighborhood.trim() || undefined,
                city: companyData.city.trim() || undefined,
                cityCode: companyData.cityCode.trim() || undefined,
                state: companyData.state.trim() || undefined,
                country: companyData.country || undefined,
                segment: companyData.segment || undefined,
                secondarySegment: companyData.secondarySegment || undefined,
                whatsappNumber: companyData.whatsappNumber.trim() || undefined,
            })

            if (response.success && response.result) {
                toast.success('Dados da empresa atualizados com sucesso')
                const updatedCompany = response.result as any
                dispatch(
                    setCredentials({
                        ...auth,
                        user: {
                            ...user,
                            company: {
                                ...user.company,
                                ...updatedCompany,
                            },
                        },
                    })
                )
                // Atualizar a empresa no array de accessibleCompanies para refletir no CompanySelector
                const updatedAccessible = auth.accessibleCompanies.map(c =>
                    c.id === user.companyId
                        ? {
                              ...c,
                              name: updatedCompany.name,
                              tradingName: updatedCompany.tradingName,
                              logoUrl: updatedCompany.logoUrl,
                          }
                        : c
                )
                dispatch(setAccessibleCompanies(updatedAccessible))
            } else {
                toast.error(
                    response.error || 'Erro ao atualizar dados da empresa'
                )
            }
        } catch (error) {
            toast.error('Erro ao atualizar dados da empresa')
        } finally {
            setLoadingCompany(false)
        }
    }

    const handleCepChange = async (cep: string) => {
        const cleanCep = cep.replace(/\D+/g, '')

        if (cleanCep.length !== 8) {
            return
        }

        setLoadingCep(true)
        try {
            const address = await fetchPostalCode(cleanCep)

            setCompanyData(prev => ({
                ...prev,
                zipCode: address.cep || prev.zipCode,
                street: address.logradouro || prev.street,
                neighborhood: address.bairro || prev.neighborhood,
                city: address.localidade || prev.city,
                cityCode: address.ibge || prev.cityCode,
                state: address.uf || prev.state,
            }))
        } catch (error: any) {
            toast.error('CEP não encontrado' + error.message)
        } finally {
            setLoadingCep(false)
        }
    }

    return (
            <div className="flex flex-col gap-6">
                <h2 className="text-xl font-bold text-base-content">
                    Dados da Empresa
                </h2>

                <div className="card bg-base-200 p-6">
                    <h3 className="font-semibold text-base-content mb-4">
                        {isPartner
                            ? 'Logo do Parceiro'
                            : 'Logo da Empresa'}
                    </h3>
                    <div className="flex items-center gap-6">
                        <div className="w-24 h-24 rounded-lg bg-base-100 border-2 border-dashed border-base-300 flex items-center justify-center overflow-hidden">
                            {logoPreview ||
                            (isPartner
                                ? user?.partner?.logoUrl
                                : user?.company?.logoUrl) ? (
                                <img
                                    src={
                                        logoPreview ||
                                        getFileUrl(
                                            isPartner
                                                ? user?.partner
                                                      ?.logoUrl
                                                : user?.company
                                                      ?.logoUrl
                                        )
                                    }
                                    alt="Logo"
                                    className="max-w-full max-h-full object-contain"
                                />
                            ) : (
                                <MdImage className="text-3xl text-base-content/30" />
                            )}
                        </div>
                        <div className="flex flex-col gap-2">
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={e => {
                                    const file =
                                        e.target.files?.[0]
                                    // O preview sai do hook, já com o corte aplicado.
                                    if (file) handleLogoUpload(file)
                                    e.target.value = ''
                                }}
                            />
                            <button
                                className="btn btn-secondary btn-sm gap-2"
                                onClick={() =>
                                    logoInputRef.current?.click()
                                }
                                disabled={uploadingLogo}
                            >
                                {uploadingLogo ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdCloudUpload className="text-lg" />
                                )}
                                {uploadingLogo
                                    ? 'Enviando...'
                                    : 'Enviar Logo'}
                            </button>
                            <p className="text-xs text-base-content/50">
                                PNG, JPG ou SVG. Tamanho máximo:
                                5MB.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                {companyNameLabel(
                                    user?.company
                                )}
                            </span>
                        </label>
                        <div className="relative">
                            <MdBusiness className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="text"
                                placeholder={
                                    isCompanyIndividual
                                        ? 'Nome completo do titular'
                                        : 'Razão Social da Empresa'
                                }
                                className="input input-bordered w-full pl-10"
                                value={companyData.name}
                                onChange={e =>
                                    setCompanyData({
                                        ...companyData,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                {isCompanyIndividual
                                    ? 'Nome de exibição'
                                    : 'Nome Fantasia'}
                            </span>
                        </label>
                        <div className="relative">
                            <MdBusiness className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="text"
                                placeholder={
                                    isCompanyIndividual
                                        ? 'Como você quer ser chamado'
                                        : 'Nome Fantasia'
                                }
                                className="input input-bordered w-full pl-10"
                                value={companyData.tradingName}
                                onChange={e =>
                                    setCompanyData({
                                        ...companyData,
                                        tradingName:
                                            e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Segmento
                            </span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={companyData.segment}
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    segment: e.target
                                        .value as CompanySegment,
                                })
                            }
                        >
                            <option value="">
                                Selecione o segmento
                            </option>
                            <option
                                value={CompanySegment.HEALTH}
                            >
                                Saúde
                            </option>
                            <option
                                value={CompanySegment.BEAUTY}
                            >
                                Beleza e Estética
                            </option>
                            <option
                                value={CompanySegment.FITNESS}
                            >
                                Academia e Fitness
                            </option>
                            <option
                                value={CompanySegment.PET_SHOP}
                            >
                                Pet Shop e Veterinária
                            </option>
                            <option
                                value={
                                    CompanySegment.DIRECT_SALE
                                }
                            >
                                Venda com preços fixos
                            </option>
                            <option
                                value={
                                    CompanySegment.QUOTE_BASED_SALE
                                }
                            >
                                Venda com orçamento
                            </option>
                            <option
                                value={CompanySegment.OTHER}
                            >
                                Outro
                            </option>
                        </select>
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Segmento Secundário
                            </span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={companyData.secondarySegment}
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    secondarySegment:
                                        e.target.value,
                                })
                            }
                        >
                            <option value="">
                                Selecione...
                            </option>
                            <option value="INTERMEDIATION">
                                Intermediação
                            </option>
                            <option value="OTHER">Outro</option>
                        </select>
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Sobre Nós{' '}
                            <span className="text-gray-400 text-sm">
                                (Importante para os atendentes)
                            </span>
                        </span>
                    </label>
                    <textarea
                        className="textarea textarea-bordered w-full min-h-[150px]"
                        placeholder="Descreva sua empresa..."
                        value={companyData.aboutUs || ''}
                        onChange={e =>
                            setCompanyData({
                                ...companyData,
                                aboutUs: e.target.value,
                            })
                        }
                        rows={6}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                E-mail da Empresa
                            </span>
                        </label>
                        <div className="relative">
                            <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="email"
                                placeholder="contato@empresa.com"
                                className="input input-bordered w-full pl-10"
                                value={companyData.email}
                                onChange={e =>
                                    setCompanyData({
                                        ...companyData,
                                        email: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Website
                            </span>
                        </label>
                        <div className="relative">
                            <MdLanguage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="url"
                                placeholder="https://www.exemplo.com"
                                className="input input-bordered w-full pl-10"
                                value={companyData.website}
                                onChange={e =>
                                    setCompanyData({
                                        ...companyData,
                                        website: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                WhatsApp da Empresa
                            </span>
                        </label>
                        <div className="relative">
                            <FaWhatsapp className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="tel"
                                placeholder="+55 (00) 00000-0000"
                                className="input input-bordered w-full pl-10"
                                value={formatPhone(
                                    companyData.whatsappNumber
                                )}
                                onChange={e => {
                                    const raw = e.target.value
                                        .replace(/\D/g, '')
                                        .slice(0, 13)
                                    setCompanyData({
                                        ...companyData,
                                        whatsappNumber: raw,
                                    })
                                }}
                            />
                        </div>
                    </div>
                </div>

                {isCompanyIndividual ? (
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                CPF do titular
                                <span className="text-error ml-1">
                                    *
                                </span>
                            </span>
                        </label>
                        <div className="relative">
                            <MdPerson className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="000.000.000-00"
                                className="input input-bordered w-full pl-10"
                                value={companyData.cpf}
                                onChange={e =>
                                    setCompanyData({
                                        ...companyData,
                                        cpf: formatCpf(
                                            e.target.value
                                        ),
                                    })
                                }
                                maxLength={14}
                            />
                        </div>
                        <label className="label">
                            <span className="label-text-alt text-base-content/60">
                                Conta de pessoa física: o
                                documento da conta é o CPF do
                                titular.
                            </span>
                        </label>
                    </div>
                ) : (
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                CNPJ
                                <span className="text-error ml-1">
                                    *
                                </span>
                            </span>
                        </label>
                        <div className="relative">
                            <MdBusiness className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="00.000.000/0000-00"
                                className="input input-bordered w-full pl-10"
                                value={companyData.cnpj}
                                onChange={e =>
                                    setCompanyData({
                                        ...companyData,
                                        cnpj: formatCnpj(
                                            e.target.value
                                        ),
                                    })
                                }
                                maxLength={18}
                            />
                        </div>
                    </div>
                )}

                {/*
                    Dados fiscais só existem para pessoa jurídica:
                    inscrição estadual/municipal, item da LC 116 e
                    regime tributário são cadastros do fisco que PF
                    não possui, e sem eles não há emissão de nota.
                */}
                {!isCompanyIndividual && (
                    <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Inscrição Estadual{' '}
                                <span className="text-base-content/40"></span>
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="Inscrição Estadual"
                            className="input input-bordered w-full"
                            value={
                                companyData.stateRegistration
                            }
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    stateRegistration:
                                        e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text">
                                Inscrição Municipal{' '}
                                <span className="text-base-content/40"></span>
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="Inscrição Municipal"
                            className="input input-bordered w-full"
                            value={
                                companyData.municipalRegistration
                            }
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    municipalRegistration:
                                        e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Código de Serviço (LC 116)
                        </span>
                    </label>
                    <SearchableSelect
                        options={SERVICE_LIST_ITEMS.map(
                            item => ({
                                value: item.code,
                                label: item.label,
                                group: item.group,
                            })
                        )}
                        value={companyData.serviceListItem}
                        onChange={v =>
                            setCompanyData({
                                ...companyData,
                                serviceListItem: v,
                            })
                        }
                        placeholder="Buscar item de serviço..."
                    />
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Regime Tributário{' '}
                        </span>
                    </label>
                    <select
                        className="select select-bordered w-full"
                        value={companyData.taxRegime}
                        onChange={e =>
                            setCompanyData({
                                ...companyData,
                                taxRegime: e.target.value,
                            })
                        }
                    >
                        <option value="">
                            Selecione o regime tributário
                        </option>
                        <option value="Simples Nacional">
                            Simples Nacional
                        </option>
                        <option value="Lucro Presumido">
                            Lucro Presumido
                        </option>
                        <option value="Lucro Real">
                            Lucro Real
                        </option>
                        <option value="MEI">MEI</option>
                    </select>
                </div>
                    </>
                )}

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">CEP</span>
                    </label>
                    <div className="relative">
                        <MdLocationOn className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="00000-000"
                            className="input input-bordered w-full pl-10"
                            value={companyData.zipCode}
                            onChange={e => {
                                const value = e.target.value
                                const formattedValue =
                                    formatCep(value)
                                setCompanyData({
                                    ...companyData,
                                    zipCode: formattedValue,
                                })
                                if (
                                    formattedValue.replace(
                                        /\D+/g,
                                        ''
                                    ).length === 8
                                ) {
                                    handleCepChange(
                                        formattedValue
                                    )
                                }
                            }}
                            maxLength={9}
                        />
                        {loadingCep && (
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 loading loading-spinner loading-sm z-10"></span>
                        )}
                    </div>
                </div>

                <div className="flex gap-2">
                    <div className="form-control flex-1">
                        <label className="label">
                            <span className="label-text">
                                Rua
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="Rua, Avenida, etc."
                            className="input input-bordered w-full"
                            value={companyData.street}
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    street: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="form-control w-32">
                        <label className="label">
                            <span className="label-text">
                                Número
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="123"
                            className="input input-bordered w-full"
                            value={companyData.number}
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    number: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Complemento
                        </span>
                    </label>
                    <input
                        type="text"
                        placeholder="Apto, Sala, etc."
                        className="input input-bordered w-full"
                        value={companyData.complement}
                        onChange={e =>
                            setCompanyData({
                                ...companyData,
                                complement: e.target.value,
                            })
                        }
                    />
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Bairro
                        </span>
                    </label>
                    <input
                        type="text"
                        placeholder="Bairro"
                        className="input input-bordered w-full"
                        value={companyData.neighborhood}
                        onChange={e =>
                            setCompanyData({
                                ...companyData,
                                neighborhood: e.target.value,
                            })
                        }
                    />
                </div>

                <div className="flex gap-2">
                    <div className="form-control flex-1">
                        <label className="label">
                            <span className="label-text">
                                Cidade
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="Cidade"
                            className="input input-bordered w-full"
                            value={companyData.city}
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    city: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="form-control w-32">
                        <label className="label">
                            <span className="label-text">
                                Estado
                            </span>
                        </label>
                        <input
                            type="text"
                            placeholder="UF"
                            className="input input-bordered w-full"
                            value={companyData.state}
                            onChange={e =>
                                setCompanyData({
                                    ...companyData,
                                    state: e.target.value.toUpperCase(),
                                })
                            }
                            maxLength={2}
                        />
                    </div>
                </div>

                <button
                    className="btn btn-secondary w-full"
                    onClick={handleUpdateCompany}
                    disabled={loadingCompany}
                >
                    {loadingCompany ? (
                        <span className="loading loading-spinner"></span>
                    ) : (
                        'Salvar Alterações'
                    )}
                </button>
                {logoCropDialog}
            </div>
    )
}
